// University of North Carolina at Charlotte: public college page (#2840),
// official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UNC Charlotte's Common Data Set 2025-2026 (CDS_2025-2026.xlsx, linked
//    from Institutional Research's Common Data Set page): C1 applicants,
//    admits and enrollees for Fall 2025 with the residency table, C8 test
//    policy for Fall 2027, C21/C22 plans. Its G1/G5 costs are for 2025-2026,
//    so the cost table uses the admissions office's 2026-2027 figures.
//  - Undergraduate Admissions: Application Requirements & Deadlines (Fall
//    2027 test rule, deadlines and notification dates) and Application
//    Review (competitive majors). Quirk: the requirements page says scores
//    are required for a weighted GPA "of 2.8 or lower", the review page says
//    test optional for "2.8 or higher", and the CDS says "less than 2.80";
//    the testing line says so.
//  - College of Arts + Architecture admissions pages for Art and Graphic
//    Design (portfolio), the School of Architecture (secondary application,
//    Fall 2027 calendar) and Music (audition, 2027 dates). The art page's
//    portfolio deadline is February 10, 2027, but its line on when to apply
//    to the university still names 2025 and 2026 dates, so that line is not
//    used.
//  - The 2026-2027 Undergraduate Catalog's Artificial Intelligence, B.S.
//    page (freshman math minimums).
//  - Undergraduate Admissions, Cost of Attendance, 2026-2027, on campus.
//    The table includes $103 in loan fees, and transportation differs by
//    residency ($1,340 and $2,000).
//  - College Scorecard, school id 199139 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UNC Charlotte Common Data Set 2025-2026',
        url: 'https://ir.charlotte.edu/home/common-data-set/',
        read: '2026-10-01',
    },
    requirements: {
        label: 'UNC Charlotte Admissions, Application Requirements & Deadlines',
        url: 'https://admissions.charlotte.edu/apply/first-year-students/application-requirements/',
        read: '2026-10-01',
    },
    review: {
        label: 'UNC Charlotte Admissions, Application Review',
        url: 'https://admissions.charlotte.edu/apply/first-year-students/application-review/',
        read: '2026-10-01',
    },
    art: {
        label: 'UNC Charlotte Department of Art & Art History, Admissions',
        url: 'https://coaa.charlotte.edu/art-art-history/admissions/',
        read: '2026-10-01',
    },
    architecture: {
        label: 'UNC Charlotte School of Architecture, Undergraduate Admissions',
        url: 'https://coaa.charlotte.edu/architecture/admissions/undergraduate-admissions/',
        read: '2026-10-01',
    },
    music: {
        label: 'UNC Charlotte Department of Music, Admissions',
        url: 'https://coaa.charlotte.edu/music/admissions/',
        read: '2026-10-01',
    },
    aiProgram: {
        label: 'UNC Charlotte 2026-2027 Undergraduate Catalog, Artificial Intelligence, B.S.',
        url: 'https://catalog.charlotte.edu/preview_program.php?catoid=42&poid=14023',
        read: '2026-10-01',
    },
    cost: {
        label: 'UNC Charlotte Admissions, Cost of Attendance 2026-2027',
        url: 'https://admissions.charlotte.edu/financing-your-education/cost-of-attendance/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?199139-University-of-North-Carolina-at-Charlotte',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-north-carolina-at-charlotte',
    kbId: 'university_of_north_carolina_at_charlotte',
    name: 'University of North Carolina at Charlotte',
    shortName: 'UNC Charlotte',
    aliases: ['UNC Charlotte'],
    location: 'Charlotte, NC',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (brand.charlotte.edu color palette); decoration only.
    branding: {
        primary: '#005035',
        secondary: '#A49665',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_north_carolina_at_charlotte_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 27218,
        admitted: 21170,
        enrolled: 4934,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 18398, admitted: 15202 },
            { group: 'Out-of-state', applied: 8106, admitted: 5419 },
            { group: 'International', applied: 714, admitted: 549 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UNC Charlotte\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'UNC Charlotte has no early decision plan. Its early action plan is not restrictive, and the Common Data Set does not report how many applied or were admitted early, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to UNC Charlotte and name a major. Some majors ask for more:',
        rows: [
            { school: 'Business, Nursing, Computer Science and Engineering', requirement: 'Competitive majors that require an above average academic profile. If you are competitive for the university but not for one of these majors, you may be admitted as an undeclared student.', source: 'review' },
            { school: 'Artificial Intelligence', requirement: 'Admission is competitive. Freshmen need a minimum 2.5 GPA and, if admissible, an SAT math score of 530 or an ACT math score of 22.', source: 'aiProgram' },
            { school: 'Architecture', requirement: 'A secondary application to the School of Architecture after you declare Architecture on your university application. For early action, part two is due November 15, 2026; the final deadline is February 15, 2027.', source: 'architecture' },
            { school: 'Art and Graphic Design', requirement: 'An entrance portfolio, due February 10, 2027 at noon. The department admits new art and graphic design majors for fall semesters only.', source: 'art' },
            { school: 'Music', requirement: 'An entrance audition. In-person audition dates for 2027 are January 23, February 6, February 20 (the scholarship consideration deadline) and March 20; video auditions are also accepted.', source: 'music' },
        ],
        source: 'requirements',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'By January 30', binding: false },
            { plan: 'Regular Decision', deadline: 'February 1', decision: 'By April 1', binding: false },
            { plan: 'After February 1', deadline: 'As space allows', decision: 'After April 1, as space allows', binding: false },
        ],
        note: 'Transcripts, the $75 fee or a fee waiver, the essay and any test scores must arrive within two weeks of the deadline (November 15 or February 15) for a decision by the notification date. Deadlines that fall on a weekend move to 11:59 p.m. the following Monday.',
        source: 'requirements',
        binding: 'Decisions are non-binding.',
        bindingSource: 'requirements',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'For fall 2027, SAT or ACT scores are required only from applicants with a weighted GPA of 2.8 or lower; everyone else may apply without them or self-report scores. The pages differ at exactly 2.8: the application review page says test optional at 2.8 or higher, and the Common Data Set says scores are required below 2.80. The CLT is an optional extra and cannot replace a required SAT or ACT.',
        source: 'requirements',
        cdsLine: 'The Common Data Set marks the SAT or ACT as required for some fall 2027 applicants, those with a weighted high school GPA less than 2.80, with scores due by June 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'requirements',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [3926, 22018] },
            { item: 'Required fees', amounts: [3285, 3285] },
            { item: 'Loan fees', amounts: [103, 103] },
            { item: 'Books and supplies', amounts: [600, 600] },
            { item: 'Housing (on campus)', amounts: [10200, 10200] },
            { item: 'Food (meals)', amounts: [5662, 5662] },
            { item: 'Transportation', amounts: [1340, 2000] },
            { item: 'Personal expenses', amounts: [2360, 2360] },
        ],
        totals: [27476, 46228],
        note: 'For a full-time undergraduate living on campus, fall and spring. All costs are subject to change pending legislative approval.',
        source: 'cost',
        netPrice: {
            average: 15142,
            byIncome: [
                { income: '$0 to $30,000', amount: 9782 },
                { income: '$30,001 to $48,000', amount: 10240 },
                { income: '$48,001 to $75,000', amount: 12136 },
                { income: '$75,001 to $110,000', amount: 17947 },
                { income: 'Over $110,000', amount: 22525 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UNC Charlotte\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
